import { useState } from 'react'
import { ImageIcon, Upload } from 'lucide-react'
import { useEditContext } from '@/contexts/EditContext'
import { Modal } from '@/components/ui/Modal'
import { ImageUploader } from '@/components/editor/ImageUploader'
import { cn } from '@/lib/utils'

interface EditableImageProps {
  value: string
  onChange: (url: string) => void
  className?: string
  alt?: string
  aspectRatio?: 'auto' | '16:9' | '4:3' | '1:1'
  placeholder?: string
}

export function EditableImage({
  value,
  onChange,
  className,
  alt = 'Image',
  aspectRatio = 'auto',
  placeholder = 'Ajouter une image',
}: EditableImageProps) {
  const { isEditMode } = useEditContext()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const aspectRatioClass = {
    auto: '',
    '16:9': 'aspect-video',
    '4:3': 'aspect-4/3',
    '1:1': 'aspect-square',
  }[aspectRatio]

  const handleImageChange = (url: string) => {
    onChange(url)
    if (url) {
      setIsModalOpen(false)
    }
  }

  // View mode: render image normally
  if (!isEditMode) {
    if (!value) return null
    return (
      <img
        src={value}
        alt={alt}
        className={cn(aspectRatioClass, 'object-cover', className)}
      />
    )
  }

  // Edit mode: show editable image with overlay
  return (
    <>
      <div
        className={cn(
          'relative cursor-pointer group',
          aspectRatioClass,
          className
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => setIsModalOpen(true)}
      >
        {value ? (
          <img
            src={value}
            alt={alt}
            className={cn('w-full h-full object-cover', aspectRatioClass)}
          />
        ) : (
          <div
            className={cn(
              'w-full h-full flex items-center justify-center bg-gray-100 border-2 border-dashed border-gray-300',
              aspectRatioClass || 'min-h-[200px]'
            )}
          >
            <div className="text-center text-gray-400">
              <ImageIcon className="w-12 h-12 mx-auto mb-2" />
              <p>{placeholder}</p>
            </div>
          </div>
        )}

        {/* Overlay on hover */}
        <div
          className={cn(
            'absolute inset-0 bg-black/50 flex items-center justify-center transition-opacity',
            isHovered ? 'opacity-100' : 'opacity-0'
          )}
        >
          <div className="text-white text-center">
            <Upload className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm font-medium">
              {value ? 'Changer l\'image' : 'Ajouter une image'}
            </p>
          </div>
        </div>

        {/* Edit indicator ring */}
        <div
          className={cn(
            'absolute inset-0 pointer-events-none transition-all',
            'ring-2 ring-transparent',
            isHovered && 'ring-primary-500 ring-offset-2'
          )}
        />
      </div>

      {/* Upload Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Modifier l'image"
      >
        <div className="p-4">
          <ImageUploader
            value={value}
            onChange={handleImageChange}
          />
        </div>
      </Modal>
    </>
  )
}
